import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { DynamicGridComponent } from '../../../../dynamic-modules/dynamic-grid/components/dynamic-grid/dynamic-grid.component';
import { PaginatorComponent } from '../../../../ui/modules/paginator/paginator.component';
import { DspService } from '../../../../core/services/dsp.service';
import { AdxadSidebarModal } from '../../../../ui/modules/sidebar-modal/sidebar-modal.service';
import { AdxadAlerts } from '../../../../ui/modules/alerts/components/alerts/alerts.component';
import {
  Config,
  CsvRequest,
  DynamicGridAction,
  FilterChanges,
  FiltersConfig,
  GroupItem,
  GroupsConfig,
  ReportsData,
  ReportSpotItem,
  ReportsRequest,
  ToggleSpotsBlock
} from '../../../../core/interface';
import { ReportsService } from '../../reports.service';
import { SidebarFilterComponent } from '../../../../dynamic-modules/dynamic-filter/components/sidebar-filter/sidebar-filter.component';
import { FiltersService } from '../../../../core/services/filters.service';
import { GroupsComponent } from '../groups/groups.component';
import { BehaviorSubject, Observable, zip } from 'rxjs';
import { skip, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { ReportsWizardComponent } from '../reports-wizard/reports-wizard.component';
import { GlobicaService } from '../../../../core/services/globica.service';
import { TranslocoService } from '@jsverse/transloco';
import { LayoutService } from '../../../../layout/layout.service';
import dayjs from 'dayjs';
import { RolesService } from '../../../../core/services/roles.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'adxad-report-grid',
  templateUrl: './report-grid.component.html',
  styleUrls: ['./report-grid.component.scss'],
  providers: [FiltersService]
})
export class ReportGridComponent implements OnInit {
  @ViewChild(DynamicGridComponent, { static: true })
  dynamicGrid: DynamicGridComponent;

  @ViewChild(PaginatorComponent, { static: true })
  paginator: PaginatorComponent;

  @ViewChild(GroupsComponent, { static: true })
  groups: GroupsComponent;

  @ViewChild(ReportsWizardComponent, { static: true })
  wizard: ReportsWizardComponent;

  public isLoading = false;
  public isNoResults = false;
  public isCsvLoading = false;
  public downloadCsvUrl: string;

  private config: Config = environment;
  private destroyRef = inject(DestroyRef);

  private requestGrid$ = new BehaviorSubject<ReportsRequest>(null);
  private loadGrid$: Observable<ReportsData> = this.requestGrid$.pipe(
    skip(1),
    tap(() => {
      this.isLoading = true;
      this.resetGrid();
    }),
    switchMap(data => this.statisticService.getReports(data))
  );

  constructor(
    public filter: FiltersService,
    private dspService: DspService,
    private statisticService: ReportsService,
    private sidebarModal: AdxadSidebarModal,
    private alerts: AdxadAlerts,
    public globica: GlobicaService,
    private translate: TranslocoService,
    public layoutService: LayoutService,
    public roles: RolesService
  ) {}

  ngOnInit(): void {
    this.subscribeFilter();

    this.loadGrid$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result: ReportsData) => {
        this.isLoading = false;
        this.setGrid(result);

        if (result.meta.columns.some(x => x.customView === 'toggleBlackList') && !this.isNoResults) {
          this.globica.trackEventGoals('buttonToggleBlackList_show');
        }
      },
      error: () => (this.isLoading = false)
    });

    this.paginator.init();
    this.loadData();
  }

  /**
   * @return {boolean} disable flag
   * If selected Break down by Date hour, calendar must be selected for last 103 days
   */
  get isValidDates(): boolean {
    if (!this.groups.result.includes('dateHour')) {
      return true;
    }

    if (!this.filter.request.size || !this.filter.request.get('from')) {
      return true;
    }

    const from = dayjs(this.filter.request.get('from') as string);
    const now = dayjs();

    return now.diff(from, 'days') < 103;
  }

  /**
   * Load groups & filter configs
   * Then load grid data
   */
  loadData(): void {
    zip(this.statisticService.getGroups(), this.dspService.getFilters('reports'))
      .pipe(
        tap(() => (this.isLoading = true)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: ([groups, filters]: [GroupsConfig, FiltersConfig]) => {
          this.isLoading = false;

          this.groups.setConfig(groups);
          this.filter.init({ data: filters.data });
          this.loadGrid();
        },
        error: () => (this.isLoading = false)
      });
  }

  /**
   * Subscribe on filter changes
   */
  subscribeFilter(): void {
    this.filter.changeFilter.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: FilterChanges) => {
      if (data && data.submit) {
        this.paginator.resetPage();
        this.loadGrid();
      }
    });
  }

  /**
   * Create new request in requestGrid$
   * If sort not set & exist on of next groups: dateHour, date, week, month, set one of them in sort
   * Create csvUrl
   */
  loadGrid(): void {
    if (!this.isValidDates) {
      this.alerts.error(this.translate.translate('dateHourAlert'), 5000);
      return;
    }

    let sort = this.dynamicGrid?.sort?.active;
    let direction = this.dynamicGrid?.sort?.direction;
    const groups = this.groups.result;
    const filter = this.filter.request;

    if (!sort || !this.dynamicGrid?.isUserSetSort) {
      const group = ['dateHour', 'date', 'week', 'month'].find(x => groups.indexOf(x) !== -1);
      if (group) {
        sort = group;
        direction = 'asc';
        this.dynamicGrid.setSort(sort, direction);
      }
    }

    const request = {
      limit: this.paginator.limit,
      page: this.paginator.page,
      filter,
      groups,
      sort,
      direction
    } as ReportsRequest;
    this.requestGrid$.next(request);

    const csvRequest = { filter, groups, sort, direction } as CsvRequest;
    this.downloadCsvUrl = this.config.report_csv_endpoint + this.dspService.prepareRequest(csvRequest);
  }

  /**
   * Set reports grid
   */
  setGrid(result: ReportsData): void {
    if (result.data.length && result.meta.total > 0) {
      this.globica.trackEventGoals('resultStat_show', {
        rows_number: result.meta.total
      });
    }

    // if (result.data.length && this.wizard) {
    //   this.wizard.init();
    // }

    if (!result || !result.meta || !result.meta.columns) {
      this.alerts.error(result.message || this.translate.translate('alert_somethingWentWrong'), 3000);
      return;
    }

    this.isNoResults = !result.data.length;

    if (!this.isNoResults) {
      this.dynamicGrid.setGrid(result);
      this.paginator.createList(result.meta.total);
    }
  }

  /**
   * Clear dynamic grid,
   * Clear paginator,
   * not created & not found flags set false
   */
  resetGrid(): void {
    this.dynamicGrid.clearGrid();
    this.paginator.clearList();
    this.filter.markAsApplied();
    this.isNoResults = false;
  }

  /**
   * Open sidebar filter
   */
  openFilter(): void {
    this.sidebarModal.open(SidebarFilterComponent, { data: this.filter });
  }

  /**
   * Apply filter by btn
   * Reset current page
   */
  applyFilter(): void {
    this.globica.trackEventGoals('buttonFiltersApply_click', {
      filters: this.filter.globicaFilterRequest,
      break_by: [this.groups.result]
    });

    this.paginator.resetPage();
    this.loadGrid();
  }

  /**
   * Reset current page
   * Clear filter values
   * Reload grid
   */
  clearFilters(): void {
    this.paginator.resetPage();
    this.filter.clearValues();
    this.loadGrid();
  }

  /**
   * Change order of columns
   *
   * @param {GroupItem[]} e
   */
  changeColumnsOrder(e: GroupItem[]): void {
    const filterableCols = this.dynamicGrid.columns.slice(0, e.length);
    e.forEach((group, i) => {
      this.dynamicGrid.columns[i] = filterableCols.find(x => x.id === group.id);
    });
    this.filter.markAsNotApplied();
  }

  /**
   * Toggle element in reports grid
   *
   * @param {DynamicGridAction} e
   */
  toggleFilter({ action, value }: DynamicGridAction): void {
    switch (action) {
      case 'toggleSpot': {
        const { spot, checked } = value as { spot: ReportSpotItem; checked: boolean };

        spot.isLoading = true;

        const data = {
          action: checked ? 'activate' : 'block',
          data: [
            {
              spotId: spot.id,
              campaignId: spot.campaign
            }
          ]
        } as ToggleSpotsBlock;

        this.statisticService
          .toggleSpotBlock(data)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              spot.isActive = !spot.isActive;
              spot.isLoading = false;
              this.alerts.success(
                this.translate.translate(checked ? 'alert_spotIsActivated' : 'alert_spotIsBlocked', { spot: spot.value }),
                3000
              );
            },
            error: () => (spot.isLoading = false)
          });
        break;
      }
      case 'toggleFilter': {
        this.filter.toggleFilterableValue(value.colId, value.value);
        break;
      }
    }
  }
}
